import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import { useAuth } from '../auth/AuthContext';
import * as api from '../api';
import LANGUAGES from '../i18n/languages';

export default function Header({ locale, onLocaleChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [fixed, setFixed] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoMatchId, setDemoMatchId] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 140 && !fixed) setFixed(true);
      if (window.scrollY < 5 && fixed) setFixed(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fixed]);

  useEffect(() => {
    if (fixed) {
      const timer = setTimeout(() => setAnimate(true), 10);
      return () => clearTimeout(timer);
    }
    setAnimate(false);
  }, [fixed]);

  const handleSignOut = () => {
    setUserDrawerOpen(false);
    signOut();
    navigate('/');
  };

  const handleLocaleChange = (newLocale) => {
    setLangMenuAnchor(null);
    if (onLocaleChange) onLocaleChange(newLocale);
  };

  const onDemo = async () => {
    setDemoOpen(true);
    setDemoMatchId(null);
    const demoId = Date.now();
    await api.duplicateDemo({ demoId });
    setDemoMatchId(`demo-${demoId}`);
  };

  const headerSx = {
    borderBottom: '1px solid rgba(50,48,57,0.08)',
    background: '#ffffff',
    position: fixed ? 'fixed' : 'absolute',
    height: '80px',
    top: fixed ? (animate ? 0 : '-80px') : 0,
    left: 0,
    right: 0,
    zIndex: 200,
    display: 'flex',
    alignItems: 'center',
    px: 3,
    ...(animate && {
      boxShadow: '1px 1px 4px rgba(0,0,0,0.5)',
      transition: 'all 0.3s cubic-bezier(0.34, 0.61, 0.59, 1.11) 0.4s',
    }),
  };

  return (
    <Box sx={headerSx}>
      {/* Logo */}
      <Box
        component={RouterLink}
        to="/"
        sx={{
          height: 70,
          width: 170,
          backgroundImage: 'url("/logo.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'block',
        }}
      />
      <Box sx={{ flex: 1 }} />

      {/* Language switcher */}
      <IconButton onClick={(e) => setLangMenuAnchor(e.currentTarget)} size="small" sx={{ mr: 1 }}>
        <LanguageIcon />
      </IconButton>
      <Menu
        anchorEl={langMenuAnchor}
        open={Boolean(langMenuAnchor)}
        onClose={() => setLangMenuAnchor(null)}
      >
        {LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={locale === lang.code || locale === lang.locale}
            onClick={() => handleLocaleChange(lang.locale)}
          >
            {lang.label}
          </MenuItem>
        ))}
      </Menu>

      {user ? (
        /* Logged in */
        <>
          {!isMobile && (
            <Button variant="contained" color="primary" sx={{ mx: 1.5, height: 40 }} onClick={() => navigate('/new')}>
              + {t('home.newMatch')}
            </Button>
          )}
          <Button sx={{ mx: 1.5, height: 40 }} onClick={() => setUserDrawerOpen(true)}>
            {user.name || user.email}&nbsp;<MenuIcon />
          </Button>
          <Drawer anchor="right" open={userDrawerOpen} onClose={() => setUserDrawerOpen(false)} PaperProps={{ sx: { width: 300 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1, pt: 4 }}>
              <Typography variant="body2" component={RouterLink} to="/new" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                + {t('home.newMatch')}
              </Typography>
              <Typography variant="body2" onClick={handleSignOut} sx={{ cursor: 'pointer' }}>
                {t('auth.signOut')}
              </Typography>
            </Box>
          </Drawer>
        </>
      ) : (
        /* Not logged in */
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button variant="text" sx={{ mx: 1.5, height: 40 }} href="/privacy.html">
            {t('home.privacy')}
          </Button>
          {!isMobile && (
            <>
              <Button variant="contained" color="primary" sx={{ mx: 1.5, height: 40 }} onClick={() => navigate('/new')}>
                + {t('home.newMatch')}
              </Button>
              <Button variant="contained" color="secondary" sx={{ mx: 1.5, height: 40 }} onClick={onDemo}>
                {t('home.demo')}
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Demo Modal */}
      <Modal open={demoOpen}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100vh', width: '100vw',
        }}>
          <Box sx={{
            width: 300, minHeight: demoMatchId ? 330 : 170,
            background: 'white', borderRadius: '3px',
            boxShadow: '0px 0px 5px rgba(0,0,0,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', p: 3, pb: 4, textAlign: 'center',
          }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {demoMatchId ? t('home.demoReady') : t('home.demoCreating')}
            </Typography>
            {!demoMatchId && <CircularProgress size={36} />}
            {demoMatchId && (
              <>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mb: 0.75 }}
                  component="a"
                  href={`/${demoMatchId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setDemoOpen(false)}
                >
                  {t('home.demoWebsite')}
                </Button>
                <Typography variant="caption" sx={{ mb: 1.5 }}>
                  {t('home.demoWebsiteDesc')}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mb: 0.75 }}
                  component="a"
                  href={`/${demoMatchId}/manage`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setDemoOpen(false)}
                >
                  {t('home.demoManagement')}
                </Button>
                <Typography variant="caption" sx={{ mb: 1.5 }}>
                  {t('home.demoManagementDesc')}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ mb: 0.75 }}
                  component="a"
                  href={`/${demoMatchId}/manage/scores`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setDemoOpen(false)}
                >
                  {t('home.demoScoring')}
                </Button>
                <Typography variant="caption">
                  {t('home.demoScoringDesc')}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
